/**
 * The detail card beside the tree — `organization-build-book.md` §6.8,
 * `org-08-organization-tree.png`.
 *
 * One place: who it is, where it is, who runs it, and what it holds. The four
 * tabs are `RecordTabs` rather than a set of their own, because this is the
 * same shape as a record page even though a branch is not a record anybody
 * navigates to.
 *
 * Nothing here saves as you type. A branch's code goes on visit numbers and
 * its address goes on documents, so the card holds a draft, says at the bottom
 * what it is about to change, and waits to be told.
 */
import { TIMEZONES } from '../../lib/timezones.js'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { zoneForRegion, zoneOptionLabel, zoneWords, type MeResponse, type OrgUnitDetail, type OrgUnitRow } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { request } from '../../lib/api.js'
import { formatDocumentDate } from '../../lib/format.js'
import { KindTile } from '../../builder/kind-tile.js'
import { RecordTabs } from '../../record/record-tabs.js'
import { Button, Switch, Tag } from '../primitives.js'
import { Popover } from '../overlays.js'
import { PropertyMap } from '../landing-marks.js'
import {
  ComboInput, Field, FormGrid, PhoneInput, RegionSelect, SelectInput, TextInput, Wide,
  type ComboItem,
} from '../form/index.js'
import {
  IconMore, IconNavigate, IconPin, IconPlus, IconWarn, RECORD_ICONS,
} from '../../frame/icons.js'
import { OrgYards } from './org-yards.js'
import { OrgAccessTab } from './org-access.js'

/** What the form holds while it is being edited. Strings, because inputs are. */
interface Draft {
  name: string
  code: string
  timezone: string
  /**
   * *Automatic*: the address's zone, worked out when it is saved (0137's
   * trigger), and so following the address if it moves (Aaron, Oct 23).
   */
  zoneAuto: boolean
  addressLine1: string
  city: string
  state: string
  postalCode: string
  phone: string
  email: string
  licenseNumber: string
  managerUserId: string | null
  managerName: string | null
}

const draftOf = (unit: OrgUnitDetail): Draft => ({
  name: unit.name,
  code: unit.code,
  timezone: unit.timezone ?? '',
  zoneAuto: !unit.timezone || unit.timezone === zoneForRegion('US', unit.state),
  addressLine1: unit.addressLine1 ?? '',
  city: unit.city ?? '',
  state: unit.state ?? '',
  postalCode: unit.postalCode ?? '',
  phone: unit.phone ?? '',
  email: unit.email ?? '',
  licenseNumber: unit.licenseNumber ?? '',
  managerUserId: unit.managerUserId,
  managerName: unit.managerName,
})

/**
 * What *Automatic* is (Aaron, Oct 23): the address's zone — *Eastern — from
 * the address* — else the place above's — *Eastern — same as Harrisburg
 * district* — and only with neither, plain *Automatic*.
 */
function automaticZoneWords(draft: Draft, unit: OrgUnitDetail): string {
  const fromAddress = zoneForRegion('US', draft.state)
  if (fromAddress) return `${zoneWords(fromAddress)} — from the address`
  if (unit.inheritedTimezone) return `${zoneWords(unit.inheritedTimezone)} — same as ${unit.parentName ?? 'the company'}`
  return 'Automatic'
}

/** The line under it: where the zone comes from, and that it can be changed. */
function zoneHint(draft: Draft, unit: OrgUnitDetail): string {
  if (!draft.zoneAuto) return 'chosen for this place'
  if (zoneForRegion('US', draft.state)) return 'from the address — choose another to change it'
  return `same as ${unit.parentName ?? 'the company'} until the address says otherwise`
}

const KIND_WORD: Record<OrgUnitRow['kind'], string> = {
  company: 'company', region: 'region', district: 'district', branch: 'branch',
}

export interface OrgDetailProps {
  me: MeResponse
  unit: OrgUnitDetail
  /** Every unit, for the breadcrumb path above the name. */
  units: OrgUnitRow[]
  canEdit: boolean
  tab: string
  onTab: (tab: string) => void
  onSaved: (unit: OrgUnitDetail) => void
  onMove: () => void
  onAddBelow: () => void
  onClose: () => void
  onReopen: () => void
  onSelect: (id: string) => void
}

export function OrgDetail({
  me, unit, units, canEdit, tab, onTab, onSaved, onMove, onAddBelow, onClose, onReopen,
  onSelect,
}: OrgDetailProps) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(unit))
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  // `Button` is a plain function component, so the ref that places the panel
  // goes on a wrapper with the same box.
  const menuButton = useRef<HTMLSpanElement>(null)

  // A different unit is a different form, not the same form with new values.
  useEffect(() => { setDraft(draftOf(unit)); setError(null) }, [unit.id, unit.version])

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((was) => ({ ...was, [key]: value }))

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(draftOf(unit)),
    [draft, unit])

  /*
   * A code is checked here as well as at the database, because the database's
   * answer arrives after Save and this one arrives while you type. The unique
   * index is still the rule; this is only the earlier telling of it.
   */
  const taken = useMemo(() => {
    const wanted = draft.code.trim().toUpperCase()
    if (!wanted) return null
    return units.find((one) => one.id !== unit.id && one.code.toUpperCase() === wanted) ?? null
  }, [draft.code, units, unit.id])

  const path = useMemo(() => {
    const byId = new Map(units.map((one) => [one.id, one]))
    const trail: OrgUnitRow[] = []
    let at = unit.parentId
    while (at) {
      const found = byId.get(at)
      if (!found) break
      trail.unshift(found)
      at = found.parentId
    }
    return trail
  }, [units, unit.parentId])

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const saved = await request<OrgUnitDetail>(`/org-units/${unit.id}`, {
        method: 'PUT',
        body: {
          name: draft.name,
          code: draft.code.trim().toUpperCase(),
          /* *Automatic* is sent as no zone, and the address's is written (0137). */
          timezone: draft.zoneAuto ? null : (draft.timezone || null),
          addressLine1: draft.addressLine1 || null,
          city: draft.city || null,
          state: draft.state || null,
          postalCode: draft.postalCode || null,
          phone: draft.phone || null,
          email: draft.email || null,
          licenseNumber: draft.licenseNumber || null,
          managerUserId: draft.managerUserId,
          latitude: unit.latitude,
          longitude: unit.longitude,
        },
      })
      onSaved(saved)
    } catch (problem) {
      const body = problem as { field?: string; message?: string }
      setError({ field: body.field, message: body.message ?? 'That did not save.' })
    } finally {
      setSaving(false)
    }
  }

  const word = KIND_WORD[unit.kind]
  const counts = unit.counts

  return (
    <div className={cn('flex min-w-0 flex-1 flex-col self-start overflow-hidden',
      'max-w-[940px] rounded-record border border-line bg-surface',
      // Stacked under the tree on a phone, where `flex-1` gives it no width.
      'max-lg:w-full max-lg:max-w-full')}>

      {/* Header */}
      {/* The actions drop under the name on a phone — beside it they squeezed
          "Keystone Grounds" down to "K…". */}
      <div className="flex items-center gap-3.5 px-[18px] pb-3 pt-4 max-sm:flex-wrap">
        <KindTile kind={unit.kind} size={44} />

        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex items-center gap-2">
            <b className="truncate text-[18px] -tracking-[0.01em] text-text">{unit.name}</b>
            <KindTag kind={unit.kind} />
            {!unit.active && <Tag>Closed</Tag>}
          </span>

          {path.length > 0 && (
            <span className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-text-muted">
              {path.map((step, index) => {
                const Icon = RECORD_ICONS[step.kind]
                return (
                  <span key={step.id} className="flex items-center gap-1.5">
                    {index > 0 && <span aria-hidden className="text-line-strong">›</span>}
                    <button
                      type="button"
                      onClick={() => onSelect(step.id)}
                      className="flex items-center gap-1 hover:text-text"
                    >
                      <Icon size={13} />
                      {step.name}
                    </button>
                  </span>
                )
              })}
            </span>
          )}

          <span className="flex flex-wrap gap-1.5 pt-0.5">
            {counts.yards > 0 && <Tag>{counts.yards} yard{counts.yards === 1 ? '' : 's'}</Tag>}
            {counts.zones > 0 && <Tag>{counts.zones} zone{counts.zones === 1 ? '' : 's'}</Tag>}
            {counts.crews > 0 && <Tag>{counts.crews} crew{counts.crews === 1 ? '' : 's'}</Tag>}
            {counts.people > 0 && (
              <Tag>{counts.people} {counts.people === 1 ? 'person' : 'people'}</Tag>
            )}
            {unit.parentName && <Tag>{unit.parentName}</Tag>}
          </span>
        </div>

        <span className="ml-auto flex shrink-0 gap-2 max-sm:ml-0 max-sm:w-full">
          {canEdit && unit.kind !== 'company' && (
            <Button className="h-8 text-[12.5px]" onClick={onMove}>
              <IconNavigate size={15} />Move
            </Button>
          )}
          {canEdit && unit.kind !== 'branch' && (
            <Button className="h-8 text-[12.5px]" onClick={onAddBelow}>
              <IconPlus size={15} />Add below
            </Button>
          )}
          {canEdit && (
            <>
              <span ref={menuButton} className="inline-flex">
                <Button
                  aria-label={`More for ${unit.name}`}
                  className="h-8 w-8 p-0"
                  onClick={() => setMenuOpen((was) => !was)}
                >
                  <IconMore size={16} />
                </Button>
              </span>
              <Popover
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                anchor={menuButton.current}
                align="right"
                width={212}
                label={`More for ${unit.name}`}
              >
                <div className="flex flex-col py-1">
                  {unit.active
                    ? (
                      <MenuItem
                        label={`Close ${word}`}
                        onClick={() => { setMenuOpen(false); onClose() }}
                        disabled={unit.kind === 'company'}
                      />
                    )
                    : (
                      <MenuItem
                        label={`Reopen ${word}`}
                        onClick={() => { setMenuOpen(false); onReopen() }}
                      />
                    )}
                </div>
              </Popover>
            </>
          )}
        </span>
      </div>

      <RecordTabs
        active={tab}
        onChange={onTab}
        tabs={[
          { key: 'details', label: 'Details', icon: 'details' },
          {
            key: 'yards', label: 'Yards & zones', icon: 'yards',
            count: counts.yards + counts.zones,
          },
          { key: 'access', label: 'Access', icon: 'access', count: counts.people },
          { key: 'documents', label: 'Documents', icon: 'documents' },
        ]}
      />

      {tab === 'details' && (
        <>
          <div className="flex flex-col gap-3 px-[18px] py-3.5">
            <FormGrid>
              <Field label="Name" required error={error?.field === 'name' ? error.message : undefined}>
                {({ id, describedBy, invalid }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    invalid={invalid}
                    value={draft.name}
                    readOnly={!canEdit}
                    onChange={(event) => set('name', event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Short code"
                required
                hint="on visit numbers and the schedule board"
                error={taken
                  ? `${taken.name} already uses ${taken.code}.`
                  : error?.field === 'code' ? error.message : undefined}
              >
                {({ id, describedBy, invalid }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    invalid={invalid}
                    value={draft.code}
                    readOnly={!canEdit}
                    onChange={(event) => set('code', event.target.value.toUpperCase())}
                  />
                )}
              </Field>

              <Field label="Time zone" hint={zoneHint(draft, unit)}>
                {({ id, describedBy }) => (
                  <SelectInput
                    id={id}
                    describedBy={describedBy}
                    label="Time zone"
                    value={draft.zoneAuto ? null : (draft.timezone || null)}
                    disabled={!canEdit}
                    clearable
                    clearLabel={automaticZoneWords(draft, unit)}
                    placeholder={automaticZoneWords(draft, unit)}
                    onChange={(value) => setDraft((current) => ({
                      ...current, timezone: value ?? '', zoneAuto: value === null,
                    }))}
                    options={TIMEZONES.map((zone) => ({ value: zone, label: zoneOptionLabel(zone) }))}
                  />
                )}
              </Field>

              <Field label="Status" group>
                {({ labelId }) => (
                  <span className="flex h-9 items-center gap-2.5">
                    <Switch
                      checked={unit.active}
                      label={`${unit.name} is open`}
                      hideLabel
                      disabled={!canEdit || unit.kind === 'company'}
                      onChange={(next) => (next ? onReopen() : onClose())}
                    />
                    <span aria-labelledby={labelId} className="text-[13px] text-text">
                      {unit.active
                        ? 'Open · scheduling, quoting and inventory live here'
                        : `Closed ${unit.closedAt ? `· ${formatDocumentDate(unit.closedAt)}` : ''}`}
                    </span>
                  </span>
                )}
              </Field>
            </FormGrid>

            <div className="h-px bg-line-faint" />

            <div className="grid grid-cols-[minmax(0,1fr)_220px] items-start gap-3.5 max-lg:grid-cols-1">
              <FormGrid>
                <Wide>
                  <Field label="Address">
                    {({ id }) => (
                      <TextInput
                        id={id}
                        value={draft.addressLine1}
                        readOnly={!canEdit}
                        onChange={(event) => set('addressLine1', event.target.value)}
                      />
                    )}
                  </Field>
                </Wide>

                <Field label="City">
                  {({ id }) => (
                    <TextInput
                      id={id}
                      value={draft.city}
                      readOnly={!canEdit}
                      onChange={(event) => set('city', event.target.value)}
                    />
                  )}
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  {/*
                    * A select, like every other address in the product
                    * (`address-state-field.md` §1). A branch prints on a
                    * customer's invoice, so its state being "Pa." rather than
                    * "PA" is a typo somebody else reads.
                    */}
                  <Field label="State">
                    {({ id }) => (
                      <RegionSelect
                        id={id}
                        value={draft.state}
                        disabled={!canEdit}
                        onChange={(state) => set('state', state)}
                      />
                    )}
                  </Field>
                  <Field label="ZIP">
                    {({ id }) => (
                      <TextInput
                        id={id}
                        value={draft.postalCode}
                        readOnly={!canEdit}
                        onChange={(event) => set('postalCode', event.target.value)}
                      />
                    )}
                  </Field>
                </div>

                <Field label="Phone">
                  {({ id }) => (
                    <PhoneInput
                      id={id}
                      value={draft.phone}
                      readOnly={!canEdit}
                      onChange={(value) => set('phone', value)}
                    />
                  )}
                </Field>

                <Field label="Email" error={error?.field === 'email' ? error.message : undefined}>
                  {({ id, invalid }) => (
                    <TextInput
                      id={id}
                      type="email"
                      invalid={invalid}
                      value={draft.email}
                      readOnly={!canEdit}
                      onChange={(event) => set('email', event.target.value)}
                    />
                  )}
                </Field>

                <Field
                  label="License #"
                  hint={unit.kind === 'company'
                    ? 'shown on quotes, invoices and the portal'
                    : 'inherited from the company · click to override'}
                >
                  {({ id }) => (
                    <TextInput
                      id={id}
                      value={draft.licenseNumber}
                      readOnly={!canEdit}
                      onChange={(event) => set('licenseNumber', event.target.value)}
                    />
                  )}
                </Field>

                <Field label="Manager" hint={`gets the ${word}'s approvals and exceptions`}>
                  {({ id, describedBy }) => (
                    <ComboInput
                      id={id}
                      describedBy={describedBy}
                      label="Manager"
                      thing="person"
                      disabled={!canEdit}
                      placeholder="Nobody yet"
                      value={draft.managerUserId
                        ? { id: draft.managerUserId, name: draft.managerName ?? 'Manager' }
                        : null}
                      onPick={(item) => setDraft((was) => ({
                        ...was, managerUserId: item.id, managerName: item.name,
                      }))}
                      search={searchPeople}
                    />
                  )}
                </Field>
              </FormGrid>

              <div className="flex flex-col gap-1.5">
                <span className="text-[11.5px] text-text-muted">Map pin · route start and end</span>
                <div className={cn('relative h-[150px] w-[220px] overflow-hidden rounded-[10px]',
                  'border border-line')}>
                  <PropertyMap seed={unit.id} colour={null} size={220} radius={0} pin={false} />
                  <span className={cn('absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full',
                    'text-accent-dark')}>
                    <IconPin size={22} />
                  </span>
                </div>
                <PositionNote unit={unit} />
              </div>
            </div>

            {unit.crews.length > 0 && (
              <>
                <div className="h-px bg-line-faint" />
                <div className="flex flex-col gap-2">
                  <span className="text-[11.5px] text-text-muted">Crews based here</span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {unit.crews.map((crew) => <Tag key={crew.id}>{crew.name}</Tag>)}
                  </div>
                </div>
              </>
            )}

            <div className={cn('flex items-start gap-2 text-[12px] leading-[1.45] text-text-muted')}>
              <span className="flex shrink-0 pt-px text-text-faint"><IconWarn size={14} /></span>
              <span>
                Closing a {word} keeps everything: its open visits move to the
                {' '}{unit.parentName ?? 'company'}&apos;s unassigned queue, its people keep their
                grants, and reports still show its history.
              </span>
            </div>

            {error && !error.field && (
              <p className="m-0 text-[12.5px] text-danger">{error.message}</p>
            )}
          </div>

          <div className={cn('flex items-center gap-2.5 border-t border-line-faint bg-surface-alt',
            'px-[18px] py-3')}>
            <span className="text-[12px] text-text-faint">
              {unit.updatedByName
                ? `Changed by ${unit.updatedByName}${
                  unit.updatedAt ? ` · ${formatDocumentDate(unit.updatedAt)}` : ''}`
                : 'Not changed since it was set up'}
              {` · version ${unit.version}`}
            </span>
            <span className="ml-auto flex shrink-0 gap-2">
              <Button
                className="h-8 text-[12.5px]"
                disabled={!dirty || saving}
                onClick={() => { setDraft(draftOf(unit)); setError(null) }}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                className="h-8 text-[12.5px]"
                disabled={!canEdit || saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : `Save ${word}`}
              </Button>
            </span>
          </div>
        </>
      )}

      {tab === 'yards' && (
        <OrgYards unit={unit} units={units} canEdit={canEdit} onSaved={onSaved} onSelect={onSelect} />
      )}

      {tab === 'access' && <OrgAccessTab me={me} unit={unit} canEdit={canEdit} />}

      {tab === 'documents' && <OrgDocuments me={me} unit={unit} onTab={onTab} />}
    </div>
  )
}

/**
 * What to do when a branch has no map position (Aaron, Oct 27: *"no weather
 * is showing anywhere"*). The office's position is where the forecast and the
 * calendar's drive notes start; saving an address looks it up through our
 * server, so a branch without one is told which of four things is missing —
 * `positionProblem`, the server's answer.
 */
const POSITION_FIX: Record<NonNullable<OrgUnitDetail['positionProblem']>, string> = {
  no_address: 'Type the office address and save the branch.',
  not_found: 'The address could not be placed on the map — check the street, city and ZIP, then save.',
  lookup_off: 'Address lookup is not set up yet — ask Rowted support to switch on Maps.',
  lookup_failed: 'The address lookup did not work just now — save again later, and if it keeps happening ask Rowted support.',
}

/** A position reads like the board's: *39.9401, −76.7276 · from the address*. */
const degrees = (value: number): string => value.toFixed(4).replace('-', '\u2212')

export function PositionNote({ unit }: { unit: OrgUnitDetail }) {
  if (unit.latitude !== null && unit.longitude !== null) {
    return (
      <span className="text-[11.5px] text-text-faint">
        {`${degrees(unit.latitude)}, ${degrees(unit.longitude)} · from the address`}
      </span>
    )
  }
  if (unit.kind !== 'branch') {
    return <span className="text-[11.5px] text-text-faint">No pin yet · it is set from the address</span>
  }
  return (
    <span className="flex items-start gap-1.5 text-[11.5px] leading-[1.45]" data-testid="position-note">
      <span className="flex shrink-0 pt-px text-warn"><IconWarn size={13} /></span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-text">No map position — the forecast and drive times need the office address</span>
        <span className="text-text-muted">{POSITION_FIX[unit.positionProblem ?? 'no_address']}</span>
      </span>
    </span>
  )
}

/**
 * What this unit's documents will say at the top.
 *
 * Read-only on purpose: the values are the Details tab's, and a second place
 * to type an address is a second address to keep right. What this tab adds is
 * which of them came from here and which fell through to the company — the
 * question somebody actually has when a quote goes out with the wrong phone
 * number on it.
 */
function OrgDocuments({ me, unit, onTab }: {
  me: MeResponse; unit: OrgUnitDetail; onTab: (tab: string) => void
}) {
  const lines: { label: string; value: string | null; own: boolean }[] = [
    { label: 'Name', value: unit.kind === 'company' ? unit.name : me.tenant.name, own: unit.kind === 'company' },
    { label: 'Address', value: unit.addressLine1, own: Boolean(unit.addressLine1) },
    {
      label: 'City, state ZIP',
      value: [unit.city, [unit.state, unit.postalCode].filter(Boolean).join(' ')]
        .filter(Boolean).join(', ') || null,
      own: Boolean(unit.city),
    },
    { label: 'Phone', value: unit.phone, own: Boolean(unit.phone) },
    { label: 'Email', value: unit.email, own: Boolean(unit.email) },
    { label: 'License #', value: unit.licenseNumber, own: Boolean(unit.licenseNumber) },
  ]

  return (
    <div className="flex flex-col gap-3 px-[18px] py-3.5">
      <p className="m-0 text-[12.5px] leading-[1.45] text-text-muted">
        What a quote, an invoice or the portal prints at the top when the work is
        filed under this {KIND_WORD[unit.kind]}.
      </p>

      <div className="flex flex-col rounded-card border border-line">
        {lines.map((line) => (
          <div
            key={line.label}
            className={cn('flex items-center gap-3 border-b border-line-faint px-3 py-2.5',
              'last:border-b-0')}
          >
            <span className="w-[120px] shrink-0 text-[11.5px] text-text-muted">{line.label}</span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-text">
              {line.value ?? '—'}
            </span>
            {!line.own && <Tag>from the company</Tag>}
          </div>
        ))}
      </div>

      <span className="text-[12px] text-text-faint">
        Edit under{' '}
        <button type="button" onClick={() => onTab('details')} className="font-semibold text-accent">
          Details
        </button>
        .
      </span>
    </div>
  )
}

function MenuItem({ label, onClick, disabled }: {
  label: string; onClick: () => void; disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn('flex h-9 items-center px-3 text-left text-cell text-text',
        'hover:bg-hover disabled:cursor-not-allowed disabled:opacity-50')}
    >
      {label}
    </button>
  )
}

/** The kind, said once beside the name. The tile already says it in colour. */
export function KindTag({ kind }: { kind: OrgUnitRow['kind'] }): ReactNode {
  const tone = kind === 'branch' ? 'ok' : 'plain'
  return <Tag tone={tone}>{KIND_WORD[kind][0]!.toUpperCase() + KIND_WORD[kind].slice(1)}</Tag>
}

/** Everybody, for the Manager combo. One request per keystroke is fine at 40 names. */
async function searchPeople(query: string): Promise<ComboItem[]> {
  const { items } = await request<{ items: { id: string; name: string; email: string }[] }>(
    `/people${query ? `?q=${encodeURIComponent(query)}` : ''}`)
  return items.map((person) => ({ id: person.id, name: person.name, sub: person.email }))
}


